import React, { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatDate, formatRelativeDate } from '@/lib/utils'
import {
  ArrowLeft, Trash2, Phone, Mail, Globe, Plus, X,
  Wrench, Briefcase, Shield, FileText, MapPin, Send, ShieldOff, AlertCircle, Loader2,
  ShieldCheck, AlertTriangle, FileCheck2, Clock, Eye, Check, XCircle,
} from 'lucide-react'
import {
  invokeDocumentScan, getComplianceDocSignedUrl,
  DOC_TYPE_LABEL, DOC_TYPE_SHORT, deriveDocState,
  type DocType, type ComplianceState,
} from '@/lib/vendorComplianceDocs'
import { toast } from 'sonner'
import { EditableField, EditableSelect } from './CompanyDetail'

const STATUSES = ['active', 'inactive', 'on-hold']
const PAYMENT_TERMS = ['net15', 'net30', 'net45', 'net60', 'due_on_receipt']
const PAYMENT_TERM_LABEL: Record<string, string> = {
  net15: 'Net 15',
  net30: 'Net 30',
  net45: 'Net 45',
  net60: 'Net 60',
  due_on_receipt: 'Due on receipt',
}

const STATUS_CFG: Record<string, { color: string; bg: string; border: string; label: string }> = {
  active:    { color: 'var(--positive)',      bg: 'var(--positive-dim)',  border: 'color-mix(in srgb, var(--positive) var(--status-tint-strong), transparent)', label: 'Active' },
  inactive:  { color: 'var(--ink-secondary)', bg: 'var(--border-subtle)', border: 'var(--border-default)',                                                     label: 'Inactive' },
  'on-hold': { color: 'var(--warning)',       bg: 'var(--warning-dim)',   border: 'color-mix(in srgb, var(--warning) var(--status-tint-strong), transparent)', label: 'On hold' },
}

function ToggleRow({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ padding: '10px 12px', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      <button onClick={() => onChange(!value)} aria-label={`Toggle ${label}`}
        style={{ width: 32, height: 18, borderRadius: 100, background: value ? 'var(--positive)' : 'var(--border-default)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 150ms' }}>
        <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'var(--ink-primary)', position: 'absolute', top: 2, left: value ? 16 : 2, transition: 'left 150ms' }} />
      </button>
    </div>
  )
}

function EmptyState({ icon: Icon, title, subtitle }: { icon: any; title: string; subtitle: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center' }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
        <Icon style={{ width: 22, height: 22, color: 'var(--ink-tertiary)' }} />
      </div>
      <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>{title}</p>
      <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, maxWidth: 260, lineHeight: 1.5 }}>{subtitle}</p>
    </div>
  )
}

export default function VendorDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'service-lines' | 'jobs'>('service-lines')
  const [serviceLineToAdd, setServiceLineToAdd] = useState('')

  const { data: vendor, isLoading } = useQuery({
    queryKey: ['vendor', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('vendors').select('*').eq('id', id).single()
      if (error) throw error
      return data
    },
    enabled: !!id,
  })

  const { data: serviceLines = [] } = useQuery({
    queryKey: ['service-lines', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('service_lines').select('*').eq('workspace_id', workspace.id).order('name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: vendorServiceLines = [] } = useQuery({
    queryKey: ['vendor-service-lines', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('vendor_service_lines').select('*').eq('vendor_id', id)
      if (error) throw error
      return data || []
    },
    enabled: !!id,
  })

  const { data: jobs = [] } = useQuery({
    queryKey: ['vendor-jobs', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('field_jobs').select('*').eq('vendor_id', id).order('scheduled_date', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: async (updates: Record<string, any>) => {
      const { error } = await supabase.from('vendors').update(updates).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', id] })
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      toast.success('Saved')
    },
    onError: (e: any) => toast.error(`Save failed: ${e.message}`),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('vendors').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      toast.success('Vendor deleted')
      navigate('/app/vendors')
    },
    onError: (e: any) => toast.error(`Delete failed: ${e.message}`),
  })

  const linkServiceLineMutation = useMutation({
    mutationFn: async (serviceLineId: string) => {
      const { error } = await supabase.from('vendor_service_lines').insert({ vendor_id: id, service_line_id: serviceLineId })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-service-lines', id] })
      queryClient.invalidateQueries({ queryKey: ['vendor-service-lines-counts'] })
      setServiceLineToAdd('')
      toast.success('Service line added')
    },
    onError: (e: any) => toast.error(`Failed: ${e.message}`),
  })

  const unlinkServiceLineMutation = useMutation({
    mutationFn: async (serviceLineId: string) => {
      const { error } = await supabase.from('vendor_service_lines')
        .delete().eq('vendor_id', id).eq('service_line_id', serviceLineId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-service-lines', id] })
      queryClient.invalidateQueries({ queryKey: ['vendor-service-lines-counts'] })
      toast.success('Service line removed')
    },
    onError: (e: any) => toast.error(`Failed: ${e.message}`),
  })

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: 'var(--canvas)' }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
      </div>
    )
  }
  if (!vendor) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-tertiary)' }}>Vendor not found</div>
  }

  const websiteUrl = vendor.website
    ? (vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`)
    : null
  const statusCfg = STATUS_CFG[vendor.status || 'active'] || STATUS_CFG.active

  const linkedIds = new Set(vendorServiceLines.map((vsl: any) => vsl.service_line_id))
  const linkedServiceLines = serviceLines.filter((sl: any) => linkedIds.has(sl.id))
  const availableServiceLines = serviceLines.filter((sl: any) => !linkedIds.has(sl.id) && sl.is_active)

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>
      <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: 'var(--surface-raised)' }}>
        <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/app/vendors') }}
          style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--ink-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--ink-secondary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
          <ArrowLeft style={{ width: 14, height: 14 }} /> Back
        </button>
        <div style={{ width: 1, height: 16, background: 'var(--border-default)' }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)' }}>{vendor.name}</span>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.border}` }}>
          {statusCfg.label}
        </span>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: 'var(--accent-dim)', color: 'var(--accent-text)', border: '1px solid var(--accent-border)' }}>
          {PAYMENT_TERM_LABEL[vendor.payment_terms] || vendor.payment_terms}
        </span>
        <button
          onClick={() => { if (confirm(`Delete ${vendor.name}? This cannot be undone.`)) deleteMutation.mutate() }}
          style={{ marginLeft: 'auto', padding: '6px 10px', background: 'color-mix(in srgb, var(--danger) var(--status-tint-faint), transparent)', border: '1px solid color-mix(in srgb, var(--danger) var(--status-tint-strong), transparent)', borderRadius: 8, color: 'var(--danger)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
          <Trash2 style={{ width: 12, height: 12 }} /> Delete
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        <div style={{ width: 300, borderRight: '1px solid var(--border-subtle)', overflow: 'auto', padding: '24px 16px', flexShrink: 0 }}>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: 20 }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 24px var(--accent-glow)', marginBottom: 12 }}>
              <Wrench style={{ width: 28, height: 28, color: 'var(--accent-ink)' }} />
            </div>
            <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 4px', wordBreak: 'break-word' }}>{vendor.name}</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 20 }}>
            <button onClick={() => vendor.phone && (window.location.href = `tel:${vendor.phone}`)} disabled={!vendor.phone}
              style={{ padding: '8px 6px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, color: vendor.phone ? 'var(--ink-primary)' : 'var(--ink-tertiary)', fontSize: 11, fontWeight: 600, cursor: vendor.phone ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Phone style={{ width: 11, height: 11 }} /> Call
            </button>
            <button onClick={() => vendor.email && (window.location.href = `mailto:${vendor.email}`)} disabled={!vendor.email}
              style={{ padding: '8px 6px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, color: vendor.email ? 'var(--ink-primary)' : 'var(--ink-tertiary)', fontSize: 11, fontWeight: 600, cursor: vendor.email ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Mail style={{ width: 11, height: 11 }} /> Email
            </button>
            <button onClick={() => websiteUrl && window.open(websiteUrl, '_blank', 'noopener,noreferrer')} disabled={!websiteUrl}
              style={{ gridColumn: '1 / -1', padding: '8px 6px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, color: websiteUrl ? 'var(--ink-primary)' : 'var(--ink-tertiary)', fontSize: 11, fontWeight: 600, cursor: websiteUrl ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Globe style={{ width: 11, height: 11 }} /> Visit website
            </button>
          </div>

          <p style={{ fontSize: 9, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 6px 12px' }}>Contact</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 16 }}>
            <EditableSelect label="Status" value={vendor.status} onSave={v => updateMutation.mutate({ status: v || 'active' })} options={STATUSES} />
            <EditableField label="Email" value={vendor.email} onSave={v => updateMutation.mutate({ email: v || null })} icon={Mail} placeholder="ops@vendor.com" />
            <EditableField label="Phone" value={vendor.phone} onSave={v => updateMutation.mutate({ phone: v || null })} icon={Phone} placeholder="+1 555 123 4567" />
            <EditableField label="Website" value={vendor.website} onSave={v => updateMutation.mutate({ website: v || null })} icon={Globe} placeholder="https://example.com" />
          </div>

          <p style={{ fontSize: 9, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 6px 12px' }}>Address</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 16 }}>
            <EditableField label="Address line 1" value={vendor.address_line1} onSave={v => updateMutation.mutate({ address_line1: v || null })} icon={MapPin} placeholder="123 Main St" />
            <EditableField label="Address line 2" value={vendor.address_line2} onSave={v => updateMutation.mutate({ address_line2: v || null })} placeholder="Suite 4B" />
            <EditableField label="City" value={vendor.city} onSave={v => updateMutation.mutate({ city: v || null })} placeholder="New York" />
            <EditableField label="State" value={vendor.state} onSave={v => updateMutation.mutate({ state: v || null })} placeholder="NY" />
            <EditableField label="ZIP" value={vendor.zip} onSave={v => updateMutation.mutate({ zip: v || null })} placeholder="10001" />
            <EditableField label="Country" value={vendor.country} onSave={v => updateMutation.mutate({ country: v || 'US' })} placeholder="US" />
          </div>

          <p style={{ fontSize: 9, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 6px 12px' }}>Operational</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 16 }}>
            <EditableField label="License number" value={vendor.license_number} onSave={v => updateMutation.mutate({ license_number: v || null })} icon={Shield} placeholder="LIC-12345" />
            <EditableField label="Insurance carrier" value={vendor.insurance_carrier} onSave={v => updateMutation.mutate({ insurance_carrier: v || null })} placeholder="State Farm" />
            <EditableField label="Insurance policy #" value={vendor.insurance_policy_number} onSave={v => updateMutation.mutate({ insurance_policy_number: v || null })} placeholder="POL-001" />
            <EditableField label="Insurance expires" value={vendor.insurance_expires_at} onSave={v => updateMutation.mutate({ insurance_expires_at: v || null })} type="date" placeholder="YYYY-MM-DD" />
            <ToggleRow label="Bonded" value={!!vendor.bonded} onChange={v => updateMutation.mutate({ bonded: v, ...(v ? {} : { bond_amount: null }) })} />
            {vendor.bonded && (
              <EditableField label="Bond amount" value={vendor.bond_amount?.toString() || null} onSave={v => updateMutation.mutate({ bond_amount: v ? Number(v) : null })} type="number" placeholder="50000" />
            )}
          </div>

          <p style={{ fontSize: 9, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 6px 12px' }}>Financial</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 16 }}>
            <EditableField label="Tax ID / EIN" value={vendor.tax_id} onSave={v => updateMutation.mutate({ tax_id: v || null })} placeholder="XX-XXXXXXX" />
            <ToggleRow label="W-9 on file" value={!!vendor.w9_on_file} onChange={v => updateMutation.mutate({ w9_on_file: v })} />
            <EditableSelect label="Payment terms" value={vendor.payment_terms} onSave={v => updateMutation.mutate({ payment_terms: v || 'net30' })} options={PAYMENT_TERMS} />
            <EditableField label="Default hourly rate" value={vendor.default_hourly_rate?.toString() || null} onSave={v => updateMutation.mutate({ default_hourly_rate: v ? Number(v) : null })} type="number" placeholder="125" />
            <EditableField label="Default currency" value={vendor.default_currency} onSave={v => updateMutation.mutate({ default_currency: v || 'USD' })} placeholder="USD" />
            <ToggleRow label="1099 eligible" value={!!vendor.is_1099_eligible} onChange={v => updateMutation.mutate({ is_1099_eligible: v })} />
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6, paddingLeft: 12 }}>Notes</label>
            <textarea defaultValue={vendor.notes || ''}
              onBlur={e => { if ((vendor.notes || '') === e.target.value) return; updateMutation.mutate({ notes: e.target.value }) }}
              placeholder="Anything worth remembering..." rows={4}
              style={{ width: '100%', padding: '10px 12px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 13, color: 'var(--ink-secondary)', outline: 'none', resize: 'vertical', lineHeight: 1.5, boxSizing: 'border-box', fontFamily: 'inherit' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'} />
          </div>

          <VendorPortalAccessSection vendorId={vendor.id} />

          <VendorComplianceStaffSection vendorId={vendor.id} workspaceId={vendor.workspace_id} />

          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 12 }}>
            <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>Created {formatDate(vendor.created_at)}</p>
            {vendor.updated_at && vendor.updated_at !== vendor.created_at && (
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>Updated {formatRelativeDate(vendor.updated_at)}</p>
            )}
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: 2, padding: '12px 24px 0', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
            {([
              { id: 'service-lines', label: 'Service lines', count: linkedServiceLines.length, icon: Wrench },
              { id: 'jobs',          label: 'Jobs',          count: jobs.length,                icon: Briefcase },
            ] as const).map(tab => {
              const Icon = tab.icon
              const active = activeTab === tab.id
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  style={{ padding: '10px 14px', background: 'none', border: 'none', borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`, color: active ? 'var(--ink-primary)' : 'var(--ink-tertiary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 120ms' }}>
                  <Icon style={{ width: 13, height: 13 }} />
                  {tab.label}
                  {tab.count > 0 && (
                    <span style={{ padding: '1px 6px', background: active ? 'var(--accent-dim)' : 'var(--border-subtle)', color: active ? 'var(--accent-text)' : 'var(--ink-tertiary)', borderRadius: 100, fontSize: 11, fontWeight: 700 }}>
                      {tab.count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
            {activeTab === 'service-lines' && (
              <div>
                {availableServiceLines.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
                    <select value={serviceLineToAdd} onChange={e => setServiceLineToAdd(e.target.value)}
                      style={{ flex: 1, maxWidth: 320, padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                      <option value="">Add a service line...</option>
                      {availableServiceLines.map((sl: any) => <option key={sl.id} value={sl.id}>{sl.name}</option>)}
                    </select>
                    <button onClick={() => serviceLineToAdd && linkServiceLineMutation.mutate(serviceLineToAdd)}
                      disabled={!serviceLineToAdd || linkServiceLineMutation.isPending} className="btn-primary">
                      <Plus style={{ width: 14, height: 14 }} /> Add
                    </button>
                  </div>
                )}
                {linkedServiceLines.length === 0 ? (
                  <EmptyState icon={Wrench} title="No service lines yet"
                    subtitle={serviceLines.length === 0
                      ? "Add service lines in Settings → Service Lines, then assign them here."
                      : "Pick from the dropdown above to assign which service lines this vendor handles."} />
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {linkedServiceLines.map((sl: any) => (
                      <div key={sl.id}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 100 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-text)' }}>{sl.name}</span>
                        <button onClick={() => unlinkServiceLineMutation.mutate(sl.id)} aria-label={`Remove ${sl.name}`}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0, display: 'flex', alignItems: 'center' }}
                          onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                          <X style={{ width: 12, height: 12 }} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'jobs' && (
              jobs.length === 0
                ? <EmptyState icon={Briefcase} title="No jobs yet" subtitle="Field jobs assigned to this vendor will appear here." />
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {jobs.map((j: any) => (
                      <button key={j.id} onClick={() => navigate(`/app/field?job=${j.id}`)}
                        style={{ textAlign: 'left', padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, transition: 'background 120ms' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}>
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <FileText style={{ width: 16, height: 16, color: 'var(--accent-text)' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 2px' }}>{j.title || 'Untitled job'}</p>
                          <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, fontFamily: 'monospace' }}>
                            {j.job_number}{j.scheduled_date ? ` · ${formatDate(j.scheduled_date)}` : ''}
                          </p>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-secondary)', textTransform: 'capitalize' }}>{j.status}</span>
                      </button>
                    ))}
                  </div>
                )
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Portal access section ────────────────────────────────────────────────
// Lives in the left column of VendorDetail, above the metadata footer.
// Mirrors ContactDetail's Portal access panel: lists existing vendor_users
// for THIS vendor, exposes role + revoke per row, and provides an Invite
// form that calls vendor-invite. The function carries the rules
// (first-admin bootstrap, last-admin protection, etc.); this UI just
// surfaces toast errors.

type VendorRole = 'admin' | 'tech'

interface VendorUserRow {
  id: string
  role: VendorRole
  full_name: string | null
  email: string | null
  last_login_at: string | null
  created_at: string
}

const VENDOR_ROLE_COPY: Record<VendorRole, { label: string; one_liner: string }> = {
  admin: { label: 'Admin', one_liner: 'Manages the team, sees everything.' },
  tech:  { label: 'Tech',  one_liner: 'Sees and works jobs.' },
}

function VendorPortalAccessSection({ vendorId }: { vendorId: string }) {
  const queryClient = useQueryClient()
  const [inviteOpen, setInviteOpen] = useState(false)

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['vendor-portal-users', vendorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_users')
        .select('id, role, full_name, email, last_login_at, created_at')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: true })
      if (error) throw error
      return (data ?? []) as VendorUserRow[]
    },
  })

  const invokeFn = async (body: Record<string, unknown>): Promise<{ data: any; error: string | null }> => {
    const { data, error } = await supabase.functions.invoke<any>('vendor-invite', { body })
    if (error) {
      const ctx = (error as { context?: { text?: () => Promise<string> } }).context
      let msg = error.message
      if (ctx && typeof ctx.text === 'function') {
        try { const text = await ctx.text(); const parsed = JSON.parse(text); msg = parsed?.error ?? msg }
        catch { /* ignore */ }
      }
      return { data: null, error: msg }
    }
    return { data, error: null }
  }

  const setRoleMutation = useMutation({
    mutationFn: async (vars: { vendor_user_id: string; role: VendorRole }) => {
      const r = await invokeFn({ action: 'set_role', ...vars })
      if (r.error) throw new Error(r.error)
    },
    onSuccess: () => {
      toast.success('Role updated')
      queryClient.invalidateQueries({ queryKey: ['vendor-portal-users', vendorId] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const revokeMutation = useMutation({
    mutationFn: async (vendor_user_id: string) => {
      const r = await invokeFn({ action: 'revoke', vendor_user_id })
      if (r.error) throw new Error(r.error)
    },
    onSuccess: () => {
      toast.success('Access revoked')
      queryClient.invalidateQueries({ queryKey: ['vendor-portal-users', vendorId] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-subtle)', paddingLeft: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
          Portal access ({users.length})
        </p>
        {!inviteOpen && (
          <button type="button" onClick={() => setInviteOpen(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: 'var(--accent)', border: 'none', borderRadius: 7, color: 'var(--accent-ink)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
            <Send style={{ width: 11, height: 11 }} /> Invite
          </button>
        )}
      </div>

      {users.length === 0 && !inviteOpen && (
        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>
          No portal users yet. The first invite at this vendor becomes the admin automatically — the handyman case.
        </p>
      )}

      {inviteOpen && (
        <VendorInviteForm
          firstAdmin={users.length === 0}
          onClose={() => setInviteOpen(false)}
          onInvited={() => {
            queryClient.invalidateQueries({ queryKey: ['vendor-portal-users', vendorId] })
            setInviteOpen(false)
          }}
          invoke={(body) => invokeFn({ ...body, vendor_id: vendorId })}
        />
      )}

      {isLoading ? (
        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>Loading…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: users.length > 0 ? 6 : 0 }}>
          {users.map(u => (
            <div key={u.id} style={{ padding: '8px 10px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>
                  {u.full_name || u.email || '—'}
                  <span style={{ fontSize: 9, marginLeft: 6, color: 'var(--ink-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {u.last_login_at ? 'Active' : 'Invited'}
                  </span>
                </p>
                <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: '2px 0 0' }}>
                  {u.email}{u.last_login_at ? ` · ${formatRelativeDate(u.last_login_at)}` : ' · not signed in yet'}
                </p>
              </div>
              <select value={u.role} onChange={e => setRoleMutation.mutate({ vendor_user_id: u.id, role: e.target.value as VendorRole })}
                disabled={setRoleMutation.isPending || revokeMutation.isPending}
                style={{ padding: '4px 6px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 10, fontWeight: 600, color: 'var(--ink-primary)', cursor: 'pointer' }}>
                <option value="admin">Admin</option>
                <option value="tech">Tech</option>
              </select>
              <button type="button" onClick={() => {
                if (confirm(`Revoke vendor portal access for ${u.full_name || u.email}? They will be signed out.`))
                  revokeMutation.mutate(u.id)
              }} disabled={setRoleMutation.isPending || revokeMutation.isPending}
                title="Revoke access"
                style={{ display: 'inline-flex', alignItems: 'center', padding: 5, background: 'transparent', border: '1px solid var(--danger-border)', borderRadius: 6, color: 'var(--danger)', cursor: 'pointer' }}>
                <ShieldOff style={{ width: 11, height: 11 }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function VendorInviteForm({ firstAdmin, onClose, onInvited, invoke }: {
  firstAdmin: boolean
  onClose: () => void
  onInvited: () => void
  invoke: (body: Record<string, unknown>) => Promise<{ data: any; error: string | null }>
}) {
  const [form, setForm] = useState({ full_name: '', email: '', role: (firstAdmin ? 'admin' : 'tech') as VendorRole })
  const [busy, setBusy] = useState(false)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    if (!form.email.trim() || !form.full_name.trim()) {
      toast.error('Email and name are required'); return
    }
    setBusy(true)
    const r = await invoke({ action: 'invite', email: form.email.trim(), full_name: form.full_name.trim(), role: form.role })
    setBusy(false)
    if (r.error) { toast.error(r.error); return }
    toast.success('Invite sent')
    onInvited()
  }

  return (
    <form onSubmit={onSubmit}
      style={{ background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
      {firstAdmin && (
        <p style={{ fontSize: 11, color: 'var(--accent-text)', margin: 0, fontWeight: 600 }}>
          <AlertCircle style={{ width: 11, height: 11, display: 'inline', verticalAlign: '-1px', marginRight: 3 }} />
          First invite at this vendor — they'll be the admin automatically.
        </p>
      )}
      <input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Full name *"
        style={{ width: '100%', padding: '8px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
      <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@example.com *" type="email"
        style={{ width: '100%', padding: '8px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
      <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value as VendorRole }))}
        disabled={firstAdmin}
        title={firstAdmin ? 'First invite is admin by convention' : ''}
        style={{ width: '100%', padding: '8px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', cursor: firstAdmin ? 'not-allowed' : 'pointer', boxSizing: 'border-box' }}>
        <option value="admin">Admin — {VENDOR_ROLE_COPY.admin.one_liner}</option>
        <option value="tech">Tech — {VENDOR_ROLE_COPY.tech.one_liner}</option>
      </select>
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" onClick={onClose} disabled={busy}
          style={{ flex: 1, padding: '7px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 7, color: 'var(--ink-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        <button type="submit" disabled={busy}
          style={{ flex: 1, padding: '7px', background: 'var(--accent)', border: 'none', borderRadius: 7, color: 'var(--accent-ink)', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
          {busy ? <Loader2 style={{ width: 11, height: 11, animation: 'spin 1s linear infinite' }} /> : <Send style={{ width: 11, height: 11 }} />}
          {busy ? 'Sending…' : 'Send invite'}
        </button>
      </div>
    </form>
  )
}

// ─── Compliance panel (staff) ─────────────────────────────────────────────
// Lives in VendorDetail's left column under the Portal access section.
// Per-doc-type status chip; pending docs expand to a review card with
// editable extracted fields + View document + Confirm/Reject. Confirm
// promotes effective/expiration dates and writes back to the legacy
// vendors columns so the existing UI surfaces (Vendors list, insurance
// row) stay truthful.

interface StaffDocRow {
  id: string
  doc_type: DocType
  status: 'pending_review' | 'confirmed' | 'rejected'
  effective_date: string | null
  expiration_date: string | null
  storage_path: string
  file_name: string | null
  extracted: Record<string, unknown>
  confirmed_fields: Record<string, unknown>
  reviewed_at: string | null
  created_at: string
}

const STAFF_STATE_TOKEN: Record<ComplianceState, { label: string; bg: string; border: string; color: string }> = {
  valid:          { label: 'Valid',             bg: 'var(--positive-dim)', border: 'var(--positive-border)', color: 'var(--positive)' },
  expiring_soon:  { label: 'Expiring ≤30d',     bg: 'var(--warning-dim)',  border: 'var(--warning-border)',  color: 'var(--warning)' },
  expired:        { label: 'Expired',           bg: 'var(--danger-dim)',   border: 'var(--danger-border)',   color: 'var(--danger)' },
  pending_review: { label: 'Pending review',    bg: 'var(--accent-dim)',   border: 'var(--accent-border)',   color: 'var(--accent-text)' },
  rejected:       { label: 'Rejected',          bg: 'var(--danger-dim)',   border: 'var(--danger-border)',   color: 'var(--danger)' },
  missing:        { label: 'Missing',           bg: 'var(--hover-bg)',     border: 'var(--border-default)',  color: 'var(--ink-tertiary)' },
}

interface VendorRequirements { w9?: boolean; cgl?: boolean; commercial_auto?: boolean; workers_comp?: boolean }

function VendorComplianceStaffSection({ vendorId, workspaceId }: { vendorId: string; workspaceId: string }) {
  const queryClient = useQueryClient()
  const ALL_TYPES: DocType[] = ['w9', 'cgl', 'commercial_auto', 'workers_comp']

  const { data: vendor } = useQuery({
    queryKey: ['vendor-required-docs-staff', vendorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendors').select('required_docs')
        .eq('id', vendorId).maybeSingle()
      if (error) throw error
      return data as { required_docs: VendorRequirements } | null
    },
  })

  const { data: docs = [] } = useQuery({
    queryKey: ['vendor-compliance-staff', vendorId],
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_documents')
        .select('id, doc_type, status, effective_date, expiration_date, storage_path, file_name, extracted, confirmed_fields, reviewed_at, created_at')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as StaffDocRow[]
    },
  })

  const setRequiredMutation = useMutation({
    mutationFn: async (next: VendorRequirements) => {
      const { error } = await supabase.from('vendors').update({ required_docs: next }).eq('id', vendorId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-required-docs-staff', vendorId] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const latestByType = useMemo(() => {
    const m: Partial<Record<DocType, StaffDocRow>> = {}
    for (const d of docs) if (!m[d.doc_type]) m[d.doc_type] = d
    return m
  }, [docs])

  const required = vendor?.required_docs ?? {}

  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-subtle)', paddingLeft: 12 }}>
      <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <ShieldCheck style={{ width: 11, height: 11 }} />
        Compliance
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {ALL_TYPES.map(t => {
          const row = latestByType[t] ?? null
          const isRequired = !!required[t]
          const state = deriveDocState({
            hasRow: !!row, status: row?.status ?? null,
            expirationDate: row?.expiration_date ?? null,
            isRequired,
          })
          const effectiveState: ComplianceState = (!row && !isRequired) ? 'missing' : state
          return (
            <StaffComplianceRow key={t}
              docType={t} isRequired={isRequired}
              state={effectiveState} row={row} workspaceId={workspaceId} vendorId={vendorId}
              onToggleRequired={() => setRequiredMutation.mutate({ ...required, [t]: !isRequired })}
              onChange={() => queryClient.invalidateQueries({ queryKey: ['vendor-compliance-staff', vendorId] })}
            />
          )
        })}
      </div>
    </div>
  )
}

function StaffComplianceRow({ docType, isRequired, state, row, workspaceId, vendorId, onToggleRequired, onChange }: {
  docType: DocType; isRequired: boolean; state: ComplianceState; row: StaffDocRow | null;
  workspaceId: string; vendorId: string;
  onToggleRequired: () => void; onChange: () => void;
}) {
  const [expanded, setExpanded] = useState(false)
  const cfg = STAFF_STATE_TOKEN[state]
  const isReviewable = row && row.status === 'pending_review'
  const dim = !isRequired && !row

  return (
    <div style={{ padding: '8px 10px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 8, opacity: dim ? 0.65 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <p style={{ flex: 1, margin: 0, fontSize: 12, fontWeight: 700, color: 'var(--ink-primary)' }}>
          {DOC_TYPE_SHORT[docType]} · {DOC_TYPE_LABEL[docType]}
        </p>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color, fontSize: 10, fontWeight: 700 }}>
          {state === 'pending_review' && <Clock style={{ width: 10, height: 10 }} />}
          {(state === 'expired' || state === 'rejected') && <AlertTriangle style={{ width: 10, height: 10 }} />}
          {state === 'valid' && <FileCheck2 style={{ width: 10, height: 10 }} />}
          {cfg.label}
        </span>
        <button type="button" onClick={onToggleRequired}
          title={isRequired ? 'Mark as not required' : 'Mark as required'}
          style={{ padding: '3px 7px', background: isRequired ? 'var(--accent-dim)' : 'transparent', border: `1px solid ${isRequired ? 'var(--accent-border)' : 'var(--border-default)'}`, borderRadius: 6, color: isRequired ? 'var(--accent-text)' : 'var(--ink-tertiary)', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
          {isRequired ? 'Required' : 'Optional'}
        </button>
      </div>
      {row?.expiration_date && (
        <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--ink-tertiary)' }}>Expires {row.expiration_date}</p>
      )}
      {isReviewable && (
        <button type="button" onClick={() => setExpanded(e => !e)}
          style={{ marginTop: 6, padding: '3px 0', background: 'none', border: 'none', color: 'var(--accent-text)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
          {expanded ? 'Hide review' : 'Review extracted fields →'}
        </button>
      )}
      {expanded && row && (
        <ReviewCard row={row} workspaceId={workspaceId} vendorId={vendorId}
          onClose={() => { setExpanded(false); onChange() }} />
      )}
    </div>
  )
}

function ReviewCard({ row, workspaceId, vendorId, onClose }: {
  row: StaffDocRow; workspaceId: string; vendorId: string; onClose: () => void
}) {
  const [fields, setFields] = useState<Record<string, string>>(() => {
    // Pre-fill from extracted (AI's output). Staff edits before confirm.
    const e = row.extracted as Record<string, unknown>
    const out: Record<string, string> = {}
    for (const k of Object.keys(e)) {
      const v = e[k]
      if (typeof v === 'string') out[k] = v
      else if (typeof v === 'number') out[k] = String(v)
      else if (v == null) out[k] = ''
    }
    return out
  })
  const [busy, setBusy] = useState(false)
  const [signedUrl, setSignedUrl] = useState<string | null>(null)

  const isError = (row.extracted as { error?: boolean }).error === true

  const onView = async () => {
    const { url } = await getComplianceDocSignedUrl(row.storage_path, 60)
    if (url) {
      setSignedUrl(url)
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      toast.error('Could not generate viewing link')
    }
  }

  const onRescan = async () => {
    setBusy(true)
    const { error } = await invokeDocumentScan(row.id)
    setBusy(false)
    if (error) toast.error(`Scan failed: ${error}`)
    else { toast.success('Re-scanning…'); onClose() }
  }

  const onConfirm = async () => {
    setBusy(true)
    // Promote dates from edited fields when valid ISO.
    const eff = isIsoDate(fields.effective_date) ? fields.effective_date : null
    const exp = isIsoDate(fields.expiration_date) ? fields.expiration_date : null
    const updates: Record<string, unknown> = {
      status: 'confirmed',
      confirmed_fields: fields,
      effective_date: eff,
      expiration_date: exp,
      reviewed_at: new Date().toISOString(),
    }
    const { error } = await supabase.from('vendor_documents').update(updates).eq('id', row.id)
    if (error) { setBusy(false); toast.error(error.message); return }

    // Write-back to legacy vendors columns (per dispatch's lean proposal —
    // existing staff UI surfaces stay truthful).
    if (row.doc_type === 'w9') {
      await supabase.from('vendors').update({ w9_on_file: true }).eq('id', vendorId)
    } else if (row.doc_type === 'cgl') {
      const patch: Record<string, unknown> = {}
      if (fields.carrier)        patch.insurance_carrier = fields.carrier
      if (fields.policy_number)  patch.insurance_policy_number = fields.policy_number
      if (exp)                   patch.insurance_expires_at = exp
      if (Object.keys(patch).length > 0) await supabase.from('vendors').update(patch).eq('id', vendorId)
    }
    // commercial_auto + workers_comp: no legacy columns to write back.

    setBusy(false)
    toast.success('Confirmed')
    onClose()
  }

  const onReject = async () => {
    setBusy(true)
    const { error } = await supabase.from('vendor_documents').update({
      status: 'rejected', reviewed_at: new Date().toISOString(),
    }).eq('id', row.id)
    setBusy(false)
    if (error) toast.error(error.message)
    else { toast.success('Marked for re-upload'); onClose() }
  }

  const keys = Object.keys(fields)
  return (
    <div style={{ marginTop: 10, padding: 12, background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {isError && (
        <p style={{ margin: 0, fontSize: 11, color: 'var(--warning)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <AlertTriangle style={{ width: 11, height: 11 }} />
          Extraction failed — review the document and enter the fields manually.
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {keys.length === 0 && (
          <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-tertiary)', fontStyle: 'italic' }}>No fields extracted. Use View document to read and enter manually.</p>
        )}
        {keys.map(k => (
          <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: '0 0 130px', fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {k.replace(/_/g, ' ')}
            </span>
            <input type="text" value={fields[k] ?? ''} onChange={e => setFields(f => ({ ...f, [k]: e.target.value }))}
              style={{ flex: 1, padding: '5px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 11, color: 'var(--ink-primary)', outline: 'none' }} />
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" onClick={onView}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 7, color: 'var(--ink-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
          <Eye style={{ width: 11, height: 11 }} /> View document
        </button>
        <button type="button" onClick={onRescan} disabled={busy}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 7, color: 'var(--ink-secondary)', fontSize: 11, fontWeight: 600, cursor: busy ? 'wait' : 'pointer' }}>
          Re-scan
        </button>
        <button type="button" onClick={onReject} disabled={busy}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', background: 'transparent', border: '1px solid var(--danger-border)', borderRadius: 7, color: 'var(--danger)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
          <XCircle style={{ width: 11, height: 11 }} /> Reject
        </button>
        <button type="button" onClick={onConfirm} disabled={busy}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', background: 'var(--accent)', border: 'none', borderRadius: 7, color: 'var(--accent-ink)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
          {busy ? <Loader2 style={{ width: 11, height: 11, animation: 'spin 1s linear infinite' }} /> : <Check style={{ width: 11, height: 11 }} />}
          Confirm
        </button>
      </div>
    </div>
  )
}

function isIsoDate(s: string | undefined): boolean {
  if (!s) return false
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false
  const d = new Date(s + 'T00:00:00Z')
  return !Number.isNaN(d.getTime())
}
